import { LeftOutlined, RightOutlined } from '@ant-design/icons'
import { initialQuery, TableParams } from '@supplement-bacon/sb-table-filters'

import Event from '@components/event/Event'
import Page from '@components/Page/Page'
import { useTableUrlSync } from '@hooks/useTableUrlSync'
import { Event as EventModel } from '@models/Event'
import { useAppDispatch, useAppSelector } from '@store/hooks'
import { getEvents, selectEvents } from '@store/membership/EventReducer'
import { SBRM, SBRMAction, useSBRM } from '@supplement-bacon/sbrm-react'
import { Entity } from '@utils/Entities'
import { Permission } from '@utils/permissions'
import {
  Button,
  Calendar,
  Card,
  DatePicker,
  Flex,
  Segmented,
  Space,
  Table,
  theme,
  Typography,
} from 'antd'
import dayjs, { Dayjs } from 'dayjs'
import isBetween from 'dayjs/plugin/isBetween'
import { useEffect } from 'react'

const { Text } = Typography

dayjs.extend(isBetween)

type ViewMode = 'month' | 'week'

const EventsPage = () => {
  const { token } = theme.useToken()
  const { isOpen } = useSBRM()
  const dispatch = useAppDispatch()
  const events = useAppSelector(selectEvents())

  const fetchEvents = () => {
    // The month view draws a 6-week grid, with the last days of the previous
    // month and the first days of the next one: fetch the whole grid, not only
    // the month. Whatever the first day of the week, the grid starts at most 6
    // days before the 1st and spans 42 days.
    const firstOfMonth = selectedDate.startOf('month')
    const [start, end] =
      viewMode === 'month'
        ? [firstOfMonth.subtract(6, 'day'), firstOfMonth.add(42, 'day')]
        : [
            selectedDate.startOf('week'),
            selectedDate.endOf('week').add(1, 'day'),
          ]
    const rangeStart = start.format('YYYY/MM/DD')
    const rangeEnd = end.format('YYYY/MM/DD')

    const newQuery: TableParams = {
      ...initialQuery,
      pagination: { ...initialQuery.pagination, pageSize: 10000 },
      filters: {
        ...initialQuery.filters,
        dates: [rangeStart, rangeEnd],
      },
    }

    dispatch(getEvents(newQuery))
  }

  const { searchParams, setSearchParams } = useTableUrlSync<EventModel>({
    onParamsChange: fetchEvents,
  })

  const viewMode = (searchParams.get('viewMode') as ViewMode) || 'month'
  const dateParam = searchParams.get('selectedDate')
  const selectedDate = dateParam ? dayjs(dateParam) : dayjs()

  const weekStart = selectedDate.startOf('week')
  const weekEnd = selectedDate.endOf('week')

  useEffect(() => {
    if (isOpen) return

    fetchEvents()
  }, [isOpen])

  const updateParams = (updates: Record<string, string | null>) => {
    setSearchParams(
      (prev) => {
        Object.entries(updates).forEach(([key, value]) => {
          if (!value || (key === 'viewMode' && value === 'month')) {
            prev.delete(key)
          } else {
            prev.set(key, value)
          }
        })
        return prev
      },
      { replace: true }
    )
  }

  const getEventsForDay = (day: Dayjs) =>
    events.filter((event) =>
      day.isBetween(dayjs(event.start), dayjs(event.end), 'day', '[]')
    )

  const dateCellRender = (value: Dayjs) => {
    const eventsByDay = getEventsForDay(value)
    return (
      <Space
        orientation="vertical"
        size={2}
        style={{ width: '100%', padding: 8 }}
      >
        {eventsByDay.map((event) => (
          <Event.CalendarCell key={event.id} model={event} />
        ))}
      </Space>
    )
  }

  const pageTitle =
    viewMode === 'month'
      ? selectedDate.format('MMMM YY')
      : `${weekStart.format('D MMM')} – ${weekEnd.format('D MMM YY')}`

  const handlePrev = () => {
    const date = selectedDate.subtract(1, viewMode).format('YYYY-MM-DD')
    updateParams({ selectedDate: date })
  }

  const handleNext = () => {
    const date = selectedDate.add(1, viewMode).format('YYYY-MM-DD')
    updateParams({ selectedDate: date })
  }

  const weekDays = Array.from({ length: 7 }, (_, i) => weekStart.add(i, 'day'))

  return (
    <Page
      permissions={Permission.manageEventsMembership}
      pageTitle={{
        title: pageTitle,
        level: 4,
        toolbar: (
          <Space className="w-full space-between">
            <Button icon={<LeftOutlined />} onClick={handlePrev} />
            <Button icon={<RightOutlined />} onClick={handleNext} />
            {viewMode === 'month' ? (
              <DatePicker
                picker="month"
                value={selectedDate}
                onChange={(date) =>
                  date &&
                  updateParams({ selectedDate: date.format('YYYY-MM-DD') })
                }
              />
            ) : (
              <DatePicker
                picker="week"
                value={selectedDate}
                onChange={(date) =>
                  date &&
                  updateParams({ selectedDate: date.format('YYYY-MM-DD') })
                }
              />
            )}
            <Button onClick={() => updateParams({ selectedDate: null })}>
              Today
            </Button>
            <Segmented
              value={viewMode}
              onChange={(value) => {
                setSearchParams(
                  (prev) => {
                    if (value === 'month') {
                      prev.delete('viewMode')
                    } else {
                      prev.set('viewMode', 'week')
                    }
                    return prev
                  },
                  { replace: true }
                )
              }}
              options={[
                { label: 'Month', value: 'month' },
                { label: 'Week', value: 'week' },
              ]}
            />
            <SBRM.Actions actions={[SBRMAction.Create]} entity={Entity.event} />
          </Space>
        ),
      }}
    >
      {viewMode === 'month' ? (
        <Card>
          <Calendar
            headerRender={() => <></>}
            cellRender={dateCellRender}
            value={selectedDate}
            onPanelChange={(newValue: Dayjs) =>
              updateParams({ selectedDate: newValue.format('YYYY-MM-DD') })
            }
            onSelect={() => false}
          />
        </Card>
      ) : (
        <Card styles={{ body: { padding: 0 } }} style={{ overflow: 'hidden' }}>
          <Table
            style={{ height: '100%' }}
            dataSource={[{}]}
            rowKey={() => 'week'}
            pagination={false}
            tableLayout="fixed"
            columns={weekDays.map((day, i) => ({
              key: day.toString(),
              width: `${100 / 7}%`,
              onHeaderCell: () => ({
                style: {
                  borderRight:
                    i < 6 ? `1px solid ${token.colorBorder}` : undefined,
                  background: day.isSame(dayjs(), 'day')
                    ? token.colorPrimaryBg
                    : undefined,
                },
              }),
              onCell: () => ({
                style: {
                  borderRight:
                    i < 6 ? `1px solid ${token.colorBorder}` : undefined,
                  verticalAlign: 'top',
                  height: 400,
                },
              }),
              title: (
                <Flex vertical align="center">
                  <Text>{day.format('ddd')}</Text>
                  <Text type="secondary">{day.format('D')}</Text>
                </Flex>
              ),
              render: () => (
                <Flex
                  vertical
                  gap={8}
                  style={{ width: '100%', height: '100%' }}
                >
                  {getEventsForDay(day).map((event) => (
                    <Event.CalendarCell wrap key={event.id} model={event} />
                  ))}
                </Flex>
              ),
            }))}
          />
        </Card>
      )}
    </Page>
  )
}

export default EventsPage
